import { useBrand } from "../brand/BrandContext";
import { Page, SectionBlock } from "../components/Page";

export function Typography() {
  const { variant } = useBrand();

  // The display family changes per variant; we cite the actual family
  // name from the active variant so this page tells the truth.
  const displayName =
    variant.fonts.display.split(",")[0]?.replace(/"/g, "") ?? "";
  const bodyName = variant.fonts.body.split(",")[0]?.replace(/"/g, "") ?? "";

  return (
    <Page
      number="04"
      title="Typography"
      eyebrow="Type system"
      intro={
        <>
          A three-voice type system: a display serif that carries the editorial
          weight, a body face for everything that needs to be read at length,
          and one hand-script reserved for warmth — never more than five percent
          of any composition.
        </>
      }
    >
      <SectionBlock title="The three voices">
        <div className="grid gap-10">
          <div>
            <div className="flex items-baseline gap-4">
              <span className="eyebrow">Display</span>
              <span className="font-flair text-[18px] text-amber">
                {displayName}
              </span>
            </div>
            <p
              className="display mt-4 text-ink"
              style={{ fontSize: "clamp(2.4rem, 6.4vw, 5rem)" }}
            >
              Ten minutes
              <br />
              in the{" "}
              <em className="italic" style={{ color: "var(--amber)" }}>
                morning.
              </em>
            </p>
            <p className="mt-3 font-body text-[13px] text-ink-soft">
              Used for hero headlines, section titles, pull-quotes. Italic for
              emphasis — never bold.
            </p>
          </div>

          <div>
            <div className="flex items-baseline gap-4">
              <span className="eyebrow">Body</span>
              <span className="font-flair text-[18px] text-amber">
                {bodyName}
              </span>
            </div>
            <p className="mt-4 max-w-[680px] font-body text-[18px] leading-[1.6] text-ink">
              The technique we teach is centuries old. It is twenty minutes a
              day, divided into ten in the morning and ten in the evening. It
              costs nothing. You don't need to be flexible, fit, or spiritual to
              begin.
            </p>
            <p className="mt-3 font-body text-[13px] text-ink-soft">
              Used for every paragraph, every form, every email. Weight 400
              default, 500 for emphasis, 600 sparingly for chips.
            </p>
          </div>

          <div>
            <div className="flex items-baseline gap-4">
              <span className="eyebrow">Flair · 5%</span>
              <span className="font-flair text-[18px] text-amber">Caveat</span>
            </div>
            <p
              className="font-flair mt-2 text-amber"
              style={{ fontSize: "clamp(2rem, 4.5vw, 3.4rem)" }}
            >
              online · come along · just for today
            </p>
            <p className="mt-3 font-body text-[13px] text-ink-soft">
              Used for badges, stamps, eyebrows on the cover, and one or two
              moments per surface. If two pieces of flair appear on the same
              page, remove one.
            </p>
          </div>

          <div>
            <div className="flex items-baseline gap-4">
              <span className="eyebrow">Devanagari</span>
              <span className="font-flair text-[18px] text-amber">
                Tiro Devanagari Sanskrit
              </span>
            </div>
            <p
              className="font-devanagari mt-2 text-amber leading-none"
              style={{ fontSize: "clamp(4rem, 9vw, 7rem)" }}
            >
              अ
            </p>
            <p className="mt-3 font-body text-[13px] text-ink-soft">
              Only ever the अ glyph as the wordmark. We never set body copy in
              Devanagari unless a sanskrit verse is being quoted with
              translation alongside.
            </p>
          </div>
        </div>
      </SectionBlock>

      <SectionBlock title="Hierarchy & scale">
        <div className="grid gap-4">
          {[
            {
              role: "Hero display",
              size: "clamp(3rem, 7vw, 5.6rem)",
              family: "display",
              weight: "var(--display-weight)",
              text: "Ten minutes.",
            },
            {
              role: "Section title",
              size: "clamp(2rem, 4.6vw, 3.4rem)",
              family: "display",
              weight: "500",
              text: "Foundation",
            },
            {
              role: "Eyebrow",
              size: "11px",
              family: "body",
              weight: "600",
              text: "WHO WE ARE",
              tracking: "0.42em",
              upper: true,
            },
            {
              role: "Lede",
              size: "19px",
              family: "body",
              weight: "400",
              text: "An ancient meditation, taught carefully — free, and open to anyone.",
            },
            {
              role: "Body",
              size: "16px",
              family: "body",
              weight: "400",
              text: "The Sunday session is one hour. We start at 10am, on a quiet floor in the Sydney CBD.",
            },
            {
              role: "Caption",
              size: "13px",
              family: "body",
              weight: "400",
              text: "Photograph: Bondi, autumn 2025",
            },
            {
              role: "Flair",
              size: "26px",
              family: "flair",
              weight: "600",
              text: "just for today",
            },
          ].map((r) => (
            <div
              key={r.role}
              className="grid items-baseline gap-3 border-t pt-3 border-rule first:border-t-0 first:pt-0 md:grid-cols-[160px_1fr_auto]"
            >
              <span className="eyebrow">{r.role}</span>
              <span
                className={`font-${r.family} text-ink`}
                style={{
                  fontSize: r.size,
                  fontWeight: r.weight,
                  letterSpacing: r.tracking,
                  textTransform: r.upper ? "uppercase" : undefined,
                }}
              >
                {r.text}
              </span>
              <span className="font-body text-[12px] text-ink-soft">
                {r.size}
              </span>
            </div>
          ))}
        </div>
      </SectionBlock>

      <SectionBlock title="Pairings">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="surface p-6" style={{ background: "var(--paper)" }}>
            <p className="eyebrow text-amber">Hero</p>
            <p className="display mt-3 text-ink" style={{ fontSize: "2.2rem" }}>
              Ten minutes <em className="italic text-amber">morning.</em>
            </p>
            <p className="mt-4 font-body text-[15px] text-ink-soft">
              Display + body lede + small flair. The headline carries; the lede
              grounds; the flair adds a touch of personality.
            </p>
            <span className="font-flair mt-2 inline-block text-[22px] text-amber">
              come along this sunday
            </span>
          </div>
          <div className="surface p-6" style={{ background: "var(--paper)" }}>
            <p className="eyebrow text-amber">Card</p>
            <p className="font-body text-[11px] tracking-[0.32em] uppercase text-amber">
              · Next session
            </p>
            <p className="mt-2 font-display text-[22px] text-ink">
              Meditation for Inner Peace
            </p>
            <p className="mt-1 font-body text-[14px] text-ink-soft">
              Sundays · 10am AEST · Sydney CBD &amp; online · Free
            </p>
          </div>
        </div>
      </SectionBlock>
    </Page>
  );
}
